import dayjs from "dayjs";
import { Trash2, Plus, Waves, Pencil } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassCard } from "@/components/GlassCard";
import { activityIcon } from "@/lib/constants";
import { useLanguage } from "@/i18n/LanguageContext";

// Stable keys for skeleton placeholders (not array indices)
const SKELETON_KEYS = ["skeleton-recent-a", "skeleton-recent-b", "skeleton-recent-c"];

const useDayLabel = () => {
  const { t, locale } = useLanguage();
  return (iso) => {
    const d = dayjs(iso);
    if (d.isSame(dayjs(), "day")) return t("common.today");
    if (d.isSame(dayjs().subtract(1, "day"), "day")) return t("common.yesterday");
    return new Date(iso).toLocaleDateString(locale, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };
};

const EmptyState = ({ onAdd }) => {
  const { t } = useLanguage();
  return (
    <GlassCard className="p-8" data-testid="progress-empty-state">
      <div className="flex flex-col items-center text-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-cyan-300/10 border border-cyan-300/25 flex items-center justify-center">
          <Waves size={22} className="text-cyan-300/80" />
        </div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          {t("progress.emptyLine1")}
          <br />
          {t("progress.emptyLine2")}
        </p>
        <Button
          data-testid="progress-empty-add-button"
          onClick={onAdd}
          className="mt-1 rounded-xl bg-cyan-300 text-slate-950 font-semibold hover:bg-cyan-200 active:scale-[0.98]"
        >
          <Plus size={16} className="mr-1" /> {t("common.addMovement")}
        </Button>
      </div>
    </GlassCard>
  );
};

const ActivityRow = ({ activity, onDelete, onEdit }) => {
  const { t } = useLanguage();
  const dayLabel = useDayLabel();
  const Icon = activityIcon(activity.type);
  return (
    <GlassCard className="p-3.5" data-testid="progress-activity-item">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 shrink-0 rounded-xl bg-cyan-300/10 border border-cyan-300/20 flex items-center justify-center">
          <Icon size={18} className="text-cyan-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {t(`types.${activity.type}`)}
            <span className="text-[hsl(var(--muted-foreground))] font-normal">
              {" "}· {activity.duration_minutes} {t("common.min")} ·{" "}
              {t(`intensities.${activity.intensity}`)}
            </span>
          </p>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            {dayLabel(activity.occurred_at)}
            {activity.note ? ` — ${activity.note}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {/* Stage 2.2: edit action */}
          <button
            data-testid={`progress-activity-edit-${activity.id}`}
            aria-label={t("progress.editAria")}
            onClick={() => onEdit(activity)}
            className="h-9 w-9 rounded-xl flex items-center justify-center text-white/35 hover:text-cyan-300 hover:bg-white/5 transition-colors"
          >
            <Pencil size={15} />
          </button>
          {/* Stage 2.2: delete now requires confirmation */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid={`progress-activity-delete-${activity.id}`}
                aria-label={t("progress.deleteAria")}
                className="h-9 w-9 rounded-xl flex items-center justify-center text-white/35 hover:text-red-400 hover:bg-white/5 transition-colors"
              >
                <Trash2 size={15} />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-2xl bg-[#0B1220] border-white/10 max-w-sm">
              <AlertDialogHeader>
                <AlertDialogTitle>{t("progress.deleteConfirmTitle")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("progress.deleteConfirmDesc")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  data-testid={`progress-activity-delete-cancel-${activity.id}`}
                  className="rounded-xl bg-white/5 border-white/10 hover:bg-white/10 hover:text-white"
                >
                  {t("progress.deleteConfirmCancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid={`progress-activity-delete-confirm-${activity.id}`}
                  onClick={() => onDelete(activity.id)}
                  className="rounded-xl bg-red-500/90 text-white hover:bg-red-500"
                >
                  {t("progress.deleteConfirmDelete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </GlassCard>
  );
};

export const RecentActivityList = ({ activities, loading, onDelete, onEdit, onAdd }) => {
  if (!loading && activities.length === 0) {
    return <EmptyState onAdd={onAdd} />;
  }

  return (
    <div data-testid="progress-recent-activities" className="space-y-2.5">
      {loading
        ? SKELETON_KEYS.map((key) => (
            <Skeleton key={key} className="h-16 rounded-2xl bg-white/10" />
          ))
        : activities.map((a) => (
            <ActivityRow key={a.id} activity={a} onDelete={onDelete} onEdit={onEdit} />
          ))}
    </div>
  );
};
